import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

interface SpotifyTrack {
  id: string;
  name: string;
  album: {
    images: Array<{
      url: string;
      height: number;
      width: number;
    }>;
    name: string;
    release_date: string;
  };
  artists?: Array<{
    id: string;
    name: string;
  }>;
  duration_ms: number;
  preview_url: string | null;
}

interface SpotifyArtist {
  id: string;
  name: string;
  images: Array<{
    url: string;
    height: number;
    width: number;
  }>;
  followers: {
    total: number;
  };
}

interface FirebaseCacheData {
  tracks: SpotifyTrack[];
  artist: SpotifyArtist | null;
  topTracks: SpotifyTrack[];
  lastUpdated: any; // Firebase Timestamp
  version: string;
}

const CACHE_VERSION = '1.1.0';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

class FirebaseCacheService {
  private collectionName = 'spotify_cache';
  private documentId = 'artist_data';
  private memoryCache: FirebaseCacheData | null = null;
  private isSyncing = false;

  /**
   * Get cached data from Firebase
   * Returns null if cache doesn't exist or is expired
   */
  async getCachedData(): Promise<FirebaseCacheData | null> {
    try {
      // Return memory cache if available
      if (this.memoryCache) {
        return this.memoryCache;
      }

      const docRef = doc(db, this.collectionName, this.documentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data() as FirebaseCacheData;

      // Check if cache is valid
      if (data.version !== CACHE_VERSION) {
        return null;
      }

      // Check if cache is expired
      if (data.lastUpdated) {
        const lastUpdatedTime = data.lastUpdated.toMillis ? data.lastUpdated.toMillis() : data.lastUpdated;
        const now = Date.now();
        const age = now - lastUpdatedTime;

        if (age > CACHE_DURATION) {
          return null;
        }
      }

      // Store in memory cache
      this.memoryCache = data;
      return data;
    } catch (error) {
      console.error('Error reading from Firebase cache:', error);
      return null;
    }
  }

  /**
   * Save data to Firebase cache
   */
  async setCachedData(data: Partial<FirebaseCacheData>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, this.documentId);
      
      const cacheData: FirebaseCacheData = {
        tracks: data.tracks || [],
        artist: data.artist || null,
        topTracks: data.topTracks || [],
        lastUpdated: serverTimestamp(),
        version: CACHE_VERSION
      };

      await setDoc(docRef, cacheData, { merge: true });
      
      // Update memory cache
      this.memoryCache = {
        ...cacheData,
        lastUpdated: Date.now() // Use current time for memory cache
      };
    } catch (error) {
      console.error('Error writing to Firebase cache:', error);
      throw error;
    }
  }

  /**
   * Update specific fields in the cache
   */
  async updateCache(updates: Partial<FirebaseCacheData>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, this.documentId);
      
      // Get current cache or use defaults
      const currentCache = await this.getCachedData();
      const defaultData = {
        tracks: [],
        artist: null,
        topTracks: []
      };
      
      // Merge with defaults to ensure all fields exist
      const dataToWrite = {
        ...(currentCache || defaultData),
        ...updates,
        lastUpdated: serverTimestamp(),
        version: CACHE_VERSION
      };
      
      // Use setDoc with merge: true to create document if it doesn't exist
      await setDoc(docRef, dataToWrite, { merge: true });

      // Update memory cache
      if (this.memoryCache) {
        this.memoryCache = {
          ...this.memoryCache,
          ...updates,
          lastUpdated: Date.now()
        };
      } else {
        // Initialize memory cache if it doesn't exist
        this.memoryCache = {
          tracks: updates.tracks || [],
          artist: updates.artist || null,
          topTracks: updates.topTracks || [],
          lastUpdated: Date.now(),
          version: CACHE_VERSION
        };
      }
    } catch (error) {
      console.error('Error updating Firebase cache:', error);
      throw error;
    }
  }

  /**
   * Check if cache needs refresh
   */
  async needsRefresh(): Promise<boolean> {
    try {
      const cachedData = await this.getCachedData();
      
      if (!cachedData) {
        return true;
      }

      if (!cachedData.lastUpdated) {
        return true;
      }

      const lastUpdatedTime = cachedData.lastUpdated.toMillis ? 
        cachedData.lastUpdated.toMillis() : cachedData.lastUpdated;
      const age = Date.now() - lastUpdatedTime;

      // Refresh if older than 12 hours
      return age > (12 * 60 * 60 * 1000);
    } catch (error) {
      console.error('Error checking cache freshness:', error);
      return true;
    }
  }

  /**
   * Clear the cache
   */
  async clearCache(): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, this.documentId);
      await setDoc(docRef, {
        tracks: [],
        artist: null,
        topTracks: [],
        lastUpdated: serverTimestamp(),
        version: CACHE_VERSION
      });
      
      this.memoryCache = null;
    } catch (error) {
      console.error('Error clearing Firebase cache:', error);
      throw error;
    }
  }

  /**
   * Mark that a sync is in progress
   */
  setSyncing(syncing: boolean): void {
    this.isSyncing = syncing;
  }

  /**
   * Check if a sync is currently in progress
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  /**
   * Get memory cache directly (for immediate access)
   */
  getMemoryCache(): FirebaseCacheData | null {
    return this.memoryCache;
  }
}

export const firebaseCacheService = new FirebaseCacheService();
export type { SpotifyTrack, SpotifyArtist, FirebaseCacheData };

